import { ReactElement, createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useConfig } from '../config/ConfigProvider';
import {
  deleteAllCreditCards,
  deleteCreditCard,
  PSP,
  selectCreditCard,
  storeAdyenCreditCard,
  storeBraintreeCreditCard,
} from '../services/creditCardService';
import { deleteCardArrayAndUpdate, selectCardAndUnselectPrevious } from '../utils/cardUtils';

import { AdyenCreditCardFormData } from './Adyen/Forms/hooks/useAdyenState.static';
import { useAppContext } from './AppContextProvider';
import { TokenizeCardResult } from './CreditCard/Braintree/BraintreeCreditCardForm';
import { OverlayIds, useOverlays } from './OverlaysProvider';
import { CreditCardData } from './StoredCreditCards/index.interfaces';

export interface CreditCardsMutationData {
  btToken?: string;
  cardToDelete?: CreditCardData;
  creditCards: CreditCardData[];
}

interface ContextValues {
  initiateCardDeletion: (creditCard: CreditCardData) => void;
  deleteCard: () => Promise<void>;
  saveCreditCard: (tokenizationResult: TokenizeCardResult) => Promise<boolean>;
  saveAdyenCreditCard: (adyenFormData: AdyenCreditCardFormData) => Promise<boolean>;
  deleteAllCards: () => Promise<boolean>;
  makeCreditCardActive: (creditCard: CreditCardData) => Promise<boolean>;
  data: CreditCardsMutationData;
}

const CreditCardsMutationContext = createContext<ContextValues>({
  initiateCardDeletion: () => null,
  saveCreditCard: () => Promise.resolve(false),
  saveAdyenCreditCard: () => Promise.resolve(false),
  deleteCard: () => Promise.resolve(),
  deleteAllCards: () => Promise.resolve(false),
  makeCreditCardActive: () => Promise.resolve(false),
  data: { creditCards: [] },
});

interface CreditCardsMutationProps {
  pspToken: string;
  psp: PSP;
  creditCards: CreditCardData[];
}

/**
 * Manages the list of credit cards of the customer, by proposing to the other components the methods to select, delete and add credit cards.
 * Each method will call the appropriate method from the creditCardService to send a request to the backend.
 */
const CreditCardsMutationProvider: React.FC<CreditCardsMutationProps> = ({
  children,
  pspToken,
  psp,
  creditCards: inputCards,
}): ReactElement => {
  const { t } = useTranslation();
  const { config } = useConfig();
  const { csrfToken } = useAppContext();
  const { showOverlay } = useOverlays();
  const [creditCards, setCreditCards] = useState<CreditCardData[]>(inputCards);
  const [cardToDelete, setCartToDelete] = useState<CreditCardData>();

  const initiateCardDeletion = (card: CreditCardData) => {
    setCartToDelete(card);
    showOverlay(OverlayIds.DELETE_CARD);
  };

  const makeCreditCardActive = async (creditCard: CreditCardData) => {
    // Optimistic UI: we display the card as selected before actually making the request
    const previousSelectedCard = creditCards.find((c) => c.selected);
    setCreditCards((currentCreditCards) =>
      selectCardAndUnselectPrevious(currentCreditCards, creditCard.pspIdentifier),
    );

    function revertOptimisticUI() {
      setCreditCards((currentCreditCards) =>
        selectCardAndUnselectPrevious(
          currentCreditCards,
          previousSelectedCard ? previousSelectedCard.pspIdentifier : null,
        ),
      );
    }

    try {
      const isSuccess = await selectCreditCard({
        csrfToken: csrfToken as string, // todo: how to be sure we do have a string and never undefined?
        creditCardIdentifier: creditCard.pspIdentifier,
        psp,
      });

      if (!isSuccess) {
        revertOptimisticUI();
      }
      return isSuccess;
    } catch (error) {
      // just catch the error in passing, to ensure to revert the UI since the card could not be selected
      revertOptimisticUI();
      throw error;
    }
  };

  const saveCreditCard = async (tokenizationResult: TokenizeCardResult) => {
    if (tokenizationResult.success) {
      try {
        const { data } = tokenizationResult;
        await storeBraintreeCreditCard({
          csrfToken: csrfToken as string,
          psp: 'BRAINTREE',
          braintreeNonce: data.nonce,
          braintreeLastFourNumber: data.details.lastFour,
          braintreeCardholderName: data.details.cardholderName,
          braintreeExpirationMonth: data.details.expirationMonth,
          braintreeExpirationYear: data.details.expirationYear,
          braintreeCardType: data.details.cardType,
          braintreeStoreCard: data.storeInVault,
          braintreeBinData: data.binData,
          braintreeBin: data.details.bin,
        });
        if (config.env !== 'local') {
          window.location.assign('/checkout/preview');
        }
        return true;
      } catch (error) {
        throw new Error(t('checkout.payment.creditcard.error.addcard.apicall'));
      }
    } else if (tokenizationResult.errorDisplayable) {
      throw new Error(t('checkout.payment.creditcard.error.addcard.apicall'));
    } else {
      return false;
    }
  };

  const saveAdyenCreditCard = (data: AdyenCreditCardFormData) => {
    return storeAdyenCreditCard({
      csrfToken: csrfToken as string,
      data,
    });
  };

  const deleteCard = async () => {
    if (!cardToDelete) {
      return;
    }

    const newSelectedCardPspIdentifier = await deleteCreditCard({
      csrfToken: csrfToken as string,
      creditCardIdentifier: cardToDelete.pspIdentifier,
      psp,
    });

    // Remove deleted and update selection if changed
    const updatedCards = deleteCardArrayAndUpdate(
      creditCards,
      cardToDelete,
      newSelectedCardPspIdentifier,
    );
    setCreditCards(updatedCards);
  };

  const deleteAllCards = async () => {
    const isSuccess = await deleteAllCreditCards({ csrfToken: csrfToken as string, psp });
    // if (isSuccess) {
    setCreditCards([]);
    // }
    return isSuccess;
  };

  useEffect(() => {
    setCreditCards(inputCards);
  }, [inputCards]);

  return (
    <CreditCardsMutationContext.Provider
      value={{
        initiateCardDeletion,
        deleteCard,
        deleteAllCards,
        makeCreditCardActive,
        saveCreditCard,
        saveAdyenCreditCard,
        data: {
          btToken: pspToken,
          cardToDelete,
          creditCards,
        },
      }}>
      {children}
    </CreditCardsMutationContext.Provider>
  );
};

export const useCreditcardService = () => {
  return useContext(CreditCardsMutationContext);
};

export default CreditCardsMutationProvider;
